import type { Input, Vec2 } from '@chessshooter/shared';
import { vec } from '@chessshooter/shared';
import type { Settings } from './settings';

// The control scheme as a pure state machine over pointer samples and key codes, so it can be tested
// without a DOM. input.ts feeds it from Pointer and keyboard events; modes call take() once per tick.
//
// Every device produces the same Input (rule 7); only the mapping differs by pointer kind:
// - touch/pen, after surviv.io (docs/STEUERUNG.md): a finger that lands on the left half of the screen
//   is the move pad, one on the right half the aim pad; further fingers on an occupied half are
//   ignored. A pad's centre is where the finger landed, or a fixed spot when the setting locks it.
//   Moving is full speed in the pad direction beyond a small dead zone. Aiming shows the direction;
//   releasing outside the inner circle fires that way, releasing inside cancels, and a quick tap
//   that never aimed fires straight ahead.
// - mouse: every press is a shot at the cursor, fired on the press, and never a pad. Desktop moves
//   with WASD; aiming while the mouse is in motion must not swallow the shot.
// WASD and the arrow keys are a second producer of the same moveDir.

export type PointerKind = 'mouse' | 'touch' | 'pen';

export interface PointerSample {
  id: number;
  kind: PointerKind;
  /** CSS px relative to the canvas */
  x: number;
  y: number;
  /** ms */
  t: number;
}

export interface InputModelOptions {
  padRadius: number;
  portraitScale: number;
  deadZone: number;
  aimStart: number;
  quickTapMs: number;
  lockedLandscape: Vec2;
  lockedPortrait: Vec2;
  turnCooldownMs: number;
  /** world units a direction shot is aimed ahead of the player */
  aimReach: number;
}

/** CSS px the browser reserves at the screen edges (notch, home indicator) */
export interface SafeArea {
  left: number;
  right: number;
  bottom: number;
}

export interface PadView {
  origin: Vec2;
  /** the thumb, clamped to the rim */
  knob: Vec2;
  radius: number;
  locked: boolean;
}

export interface TouchView {
  move: PadView | null;
  aim: PadView | null;
  aiming: boolean;
  /** unit vector: the aim pad while aiming, otherwise the facing direction */
  aimDir: Vec2;
  /** unit vector: where a quick shot goes */
  facing: Vec2;
  /** a finger or pen has been used in this session, so touch overlays make sense */
  touched: boolean;
  /** resting spots of locked pads, for drawing them while idle */
  spots: { move: Vec2 | null; aim: Vec2 | null };
}

type Role = 'move' | 'aim' | 'ignored';

interface Tracked {
  id: number;
  role: Role;
  origin: Vec2;
  current: Vec2;
  downAt: number;
  maxPull: number;
}

type PendingFire = { at: Vec2 } | { dir: Vec2 };

const KEY_DIRS: Record<string, Vec2> = {
  KeyW: { x: 0, y: -1 },
  KeyS: { x: 0, y: 1 },
  KeyA: { x: -1, y: 0 },
  KeyD: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
};

export function isMovementKey(code: string): boolean {
  return code in KEY_DIRS;
}

const NO_SAFE_AREA: SafeArea = { left: 0, right: 0, bottom: 0 };

export class InputModel {
  private readonly pointers = new Map<number, Tracked>();
  private readonly keys = new Set<string>();
  private pendingFire: PendingFire | null = null;
  private facing: Vec2 = { x: 1, y: 0 };
  private lastAimAt = Number.NEGATIVE_INFINITY;
  private touched = false;
  private mouse: Vec2 | null = null;
  private width = 1;
  private height = 1;
  private safe: SafeArea = NO_SAFE_AREA;

  constructor(
    private readonly opts: InputModelOptions,
    private readonly settings: Settings,
  ) {}

  /** Viewport size in CSS px; decides the halves, the pad size and the locked pad spots. */
  setLayout(width: number, height: number, safe: SafeArea = NO_SAFE_AREA): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.safe = safe;
  }

  padRadius(): number {
    return this.opts.padRadius * (this.height > this.width ? this.opts.portraitScale : 1);
  }

  private lockedCentre(role: 'move' | 'aim'): Vec2 {
    const off = this.height > this.width ? this.opts.lockedPortrait : this.opts.lockedLandscape;
    const y = this.height - this.safe.bottom - off.y;
    return role === 'move' ? { x: this.safe.left + off.x, y } : { x: this.width - this.safe.right - off.x, y };
  }

  private byRole(role: 'move' | 'aim'): Tracked | null {
    for (const p of this.pointers.values()) if (p.role === role) return p;
    return null;
  }

  private pull(p: Tracked): Vec2 {
    return vec.sub(p.current, p.origin);
  }

  pointerDown(s: PointerSample): void {
    if (s.kind === 'mouse') {
      this.mouse = { x: s.x, y: s.y };
      this.pendingFire = { at: { x: s.x, y: s.y } };
      return;
    }
    this.touched = true;
    const side: Role = s.x < this.width / 2 ? 'move' : 'aim';
    const role: Role = this.byRole(side) ? 'ignored' : side;
    const locked = role === 'move' ? this.settings.movePadLocked : role === 'aim' ? this.settings.aimPadLocked : false;
    const origin = locked && role !== 'ignored' ? this.lockedCentre(role) : { x: s.x, y: s.y };
    const p: Tracked = { id: s.id, role, origin, current: { x: s.x, y: s.y }, downAt: s.t, maxPull: 0 };
    this.pointers.set(s.id, p);
    // a locked pad touched away from its centre already deflects
    if (role === 'aim') this.trackAim(p, s.t);
  }

  pointerMove(s: PointerSample): void {
    if (s.kind === 'mouse') {
      this.mouse = { x: s.x, y: s.y };
      return;
    }
    const p = this.pointers.get(s.id);
    if (!p) return;
    p.current = { x: s.x, y: s.y };
    if (p.role === 'aim') this.trackAim(p, s.t);
  }

  private trackAim(p: Tracked, t: number): void {
    const d = this.pull(p);
    const len = vec.len(d);
    p.maxPull = Math.max(p.maxPull, len);
    if (len > this.opts.deadZone) {
      this.facing = vec.scale(d, 1 / len);
      this.lastAimAt = t;
    }
  }

  pointerUp(s: PointerSample): void {
    const p = this.pointers.get(s.id);
    if (!p) return;
    this.pointers.delete(s.id);
    if (p.role !== 'aim') return;
    p.current = { x: s.x, y: s.y };
    const d = this.pull(p);
    const len = vec.len(d);
    const inner = this.opts.aimStart * this.padRadius();
    if (len >= inner) {
      const dir = vec.scale(d, 1 / len);
      this.pendingFire = { dir };
      this.facing = dir;
      this.lastAimAt = s.t;
    } else if (s.t - p.downAt <= this.opts.quickTapMs && p.maxPull < inner) {
      this.pendingFire = { dir: { ...this.facing } };
    }
    // otherwise: pulled back inside the circle, the shot is cancelled
  }

  pointerCancel(id: number): void {
    this.pointers.delete(id);
  }

  /** Forgets every finger and a shot not yet taken: the board has taken over the pointers. */
  clearPointers(): void {
    this.pointers.clear();
    this.pendingFire = null;
  }

  keyDown(code: string): void {
    if (isMovementKey(code)) this.keys.add(code);
  }

  keyUp(code: string): void {
    this.keys.delete(code);
  }

  clearKeys(): void {
    this.keys.clear();
  }

  private padMove(): Vec2 | null {
    const p = this.byRole('move');
    if (!p) return null;
    const d = this.pull(p);
    const len = vec.len(d);
    return len > this.opts.deadZone ? vec.scale(d, 1 / len) : { x: 0, y: 0 };
  }

  /** The pad wins over the keys while it is held. Always a unit vector or zero. */
  moveDir(): Vec2 {
    const pad = this.padMove();
    if (pad) return pad;
    let x = 0;
    let y = 0;
    for (const code of this.keys) {
      x += KEY_DIRS[code].x;
      y += KEY_DIRS[code].y;
    }
    return vec.normalize({ x, y });
  }

  /** Last known mouse position in CSS px, for the facing tick on desktop; null until the mouse moved. */
  cursor(): Vec2 | null {
    return this.mouse;
  }

  isAiming(): boolean {
    const p = this.byRole('aim');
    return p !== null && vec.len(this.pull(p)) >= this.opts.aimStart * this.padRadius();
  }

  touch(): TouchView {
    const r = this.padRadius();
    const view = (p: Tracked | null, locked: boolean): PadView | null =>
      p ? { origin: p.origin, knob: vec.add(p.origin, vec.clampLen(this.pull(p), r)), radius: r, locked } : null;
    const aim = this.byRole('aim');
    const aiming = this.isAiming();
    return {
      move: view(this.byRole('move'), this.settings.movePadLocked),
      aim: view(aim, this.settings.aimPadLocked),
      aiming,
      aimDir: aiming && aim ? vec.normalize(this.pull(aim)) : this.facing,
      facing: this.facing,
      touched: this.touched,
      spots: {
        move: this.settings.movePadLocked ? this.lockedCentre('move') : null,
        aim: this.settings.aimPadLocked ? this.lockedCentre('aim') : null,
      },
    };
  }

  /**
   * Builds the input for one tick and consumes the pending shot, if any. `now` is the frame clock in
   * ms (same clock as the samples), `pos` the player's world position for direction shots.
   */
  take(seq: number, now: number, pos: Vec2, toWorld: (screen: Vec2) => Vec2): Input {
    const moveDir = this.moveDir();
    // moving without aiming for a while turns the player into the move direction (surviv.io)
    if (!this.byRole('aim') && (moveDir.x !== 0 || moveDir.y !== 0) && now - this.lastAimAt > this.opts.turnCooldownMs) {
      this.facing = moveDir;
    }
    const input: Input = { seq, moveDir };
    if (this.pendingFire) {
      input.fireAt = 'at' in this.pendingFire ? toWorld(this.pendingFire.at) : vec.add(pos, vec.scale(this.pendingFire.dir, this.opts.aimReach));
      this.pendingFire = null;
    }
    return input;
  }
}
